import "./App.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { PrefsProvider } from "./context/PrefsContext";
import Layout from "./components/Layout";
import CommandPalette from "./components/CommandPalette";
import RouteSeo from "./components/RouteSeo";
import InstallNudge from "./components/InstallNudge";
import Home from "./pages/Home";

// Route-level code splitting: everything except the landing page + shell is
// loaded on demand, shrinking the initial JS bundle (better LCP/INP).
const Forum = lazy(() => import("./pages/Forum"));
const ForumPost = lazy(() => import("./pages/ForumPost"));
const Leaderboard = lazy(() => import("./pages/Leaderboard"));
const Clans = lazy(() => import("./pages/Clans"));
const ClanDetail = lazy(() => import("./pages/ClanDetail"));
const Tournaments = lazy(() => import("./pages/Tournaments"));
const TournamentDetail = lazy(() => import("./pages/TournamentDetail"));
const AdminTournaments = lazy(() => import("./pages/AdminTournaments"));
const MatchPage = lazy(() => import("./pages/MatchPage"));
const Ladders = lazy(() => import("./pages/Ladders"));
const LadderDetail = lazy(() => import("./pages/LadderDetail"));
const LadderRules = lazy(() => import("./pages/LadderRules"));
const AdminLadders = lazy(() => import("./pages/AdminLadders"));
const AdminPatreon = lazy(() => import("./pages/AdminPatreon"));
const AdminSeeding = lazy(() => import("./pages/AdminSeeding"));
const Seeding = lazy(() => import("./pages/Seeding"));
const AdminSponsors = lazy(() => import("./pages/AdminSponsors"));
const AdminSocialMedia = lazy(() => import("./pages/AdminSocialMedia"));
const Patreon = lazy(() => import("./pages/Patreon"));
const VIPLounge = lazy(() => import("./pages/VIPLounge"));
const FoundersWall = lazy(() => import("./pages/FoundersWall"));
const ClanChallenge = lazy(() => import("./pages/ClanChallenge"));
const DatabasePage = lazy(() => import("./pages/Database"));
const MapPage = lazy(() => import("./pages/MapPage"));
const Guides = lazy(() => import("./pages/Guides"));
const Compare = lazy(() => import("./pages/Compare"));
const Profile = lazy(() => import("./pages/Profile"));
const Auth = lazy(() => import("./pages/Auth"));
const Friends = lazy(() => import("./pages/Friends"));
const Clips = lazy(() => import("./pages/Clips"));
const ClipWatch = lazy(() => import("./pages/ClipWatch"));
const Screenshots = lazy(() => import("./pages/Screenshots"));
const PatchNotes = lazy(() => import("./pages/PatchNotes"));
const Events = lazy(() => import("./pages/Events"));
const Challenges = lazy(() => import("./pages/Challenges"));
const LiveServer = lazy(() => import("./pages/LiveServer"));
const LoadoutBuilder = lazy(() => import("./pages/LoadoutBuilder"));
const TTKCalculator = lazy(() => import("./pages/TTKCalculator"));
const TierList = lazy(() => import("./pages/TierList"));
const RoleLevels = lazy(() => import("./pages/RoleLevels"));
const Supporter = lazy(() => import("./pages/Supporter"));
const OAuthCallback = lazy(() => import("./pages/OAuthCallback"));
const Admin = lazy(() => import("./pages/Admin"));
const RconPanel = lazy(() => import("./pages/RconPanel"));
const MatchFeed = lazy(() => import("./pages/MatchFeed"));
const Market = lazy(() => import("./pages/Market"));
const SeasonPass = lazy(() => import("./pages/SeasonPass"));
const LootDrops = lazy(() => import("./pages/LootDrops"));
const PublicProfile = lazy(() => import("./pages/PublicProfile"));
const Team = lazy(() => import("./pages/Team"));
const AdminTeam = lazy(() => import("./pages/AdminTeam"));
const Streamers = lazy(() => import("./pages/Streamers"));
const StreamerPage = lazy(() => import("./pages/StreamerPage"));
const StreamerStudio = lazy(() => import("./pages/StreamerStudio"));
const AdminStreamers = lazy(() => import("./pages/AdminStreamers"));
const AdminServers = lazy(() => import("./pages/AdminServers"));
const AdminOverlayUpdates = lazy(() => import("./pages/AdminOverlayUpdates"));
const RconToolDownload = lazy(() => import("./pages/RconToolDownload"));
const AdminHandbook = lazy(() => import("./pages/AdminHandbook"));
const AdminHandbookEdit = lazy(() => import("./pages/AdminHandbookEdit"));
const CommunityServers = lazy(() => import("./pages/CommunityServers"));
const ReferralLanding = lazy(() => import("./pages/ReferralLanding"));
const Economy = lazy(() => import("./pages/Economy"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));
const About = lazy(() => import("./pages/About"));
const Discord = lazy(() => import("./pages/Discord"));
const MatchRecap = lazy(() => import("./pages/MatchRecap"));
const Store = lazy(() => import("./pages/Store"));
const StoreProduct = lazy(() => import("./pages/StoreProduct"));
const StoreCart = lazy(() => import("./pages/StoreCart"));
const StoreCheckout = lazy(() => import("./pages/StoreCheckout"));
const StoreOrders = lazy(() => import("./pages/StoreOrders"));
const StoreOrderDetail = lazy(() => import("./pages/StoreOrderDetail"));
const StoreTrackOrder = lazy(() => import("./pages/StoreTrackOrder"));
const AdminStore = lazy(() => import("./pages/AdminStore"));
const StorePolicy = lazy(() => import("./pages/StorePolicy"));
const StoreSupport = lazy(() => import("./pages/StoreSupport"));
const StoreTicket = lazy(() => import("./pages/StoreTicket"));
const Watch = lazy(() => import("./pages/Watch"));
const Support = lazy(() => import("./pages/Support"));
const ApplyAdmin = lazy(() => import("./pages/ApplyAdmin"));
const BanList = lazy(() => import("./pages/BanList"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogArticle = lazy(() => import("./pages/BlogArticle"));
const AdminBlog = lazy(() => import("./pages/AdminBlog"));
const NotFound = lazy(() => import("./pages/NotFound"));
const PanelApp = lazy(() => import("./pages/panel/PanelApp"));

const RouteFallback = () => (
  <div className="min-h-[60vh] flex items-center justify-center text-[#D4A94A] font-mono text-sm">// LOADING</div>
);

const RequireAuth = ({ children }) => {
  const { user, ready } = useAuth();
  if (!ready) return <div className="min-h-screen flex items-center justify-center text-[#D4A94A] font-mono text-sm">// AUTHENTICATING</div>;
  if (!user) return <Navigate to="/auth" replace />;
  return children;
};

const AppInner = () => {
  const { user, setUser } = useAuth();
  return (
    <PrefsProvider user={user} setUser={setUser}>
      <BrowserRouter>
        <RouteSeo />
        <Toaster theme="dark" position="bottom-right" toastOptions={{ style: { background: "#13151B", border: "1px solid #2A2E3D", color: "#F1F5F9" } }} />
        <CommandPalette />
        <InstallNudge />
        <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route path="/auth" element={<Auth />} />
          <Route path="/oauth/callback" element={<OAuthCallback />} />
          <Route path="/panel/*" element={<PanelApp />} />
          <Route path="/supporter" element={<Supporter />} />
          <Route path="/supporter/success" element={<Supporter />} />
          <Route path="/supporter/cancel" element={<Supporter />} />
          <Route element={<Layout />}>
            <Route path="/" element={<Home />} />
            <Route path="/forum" element={<Forum />} />
            <Route path="/forum/:id" element={<ForumPost />} />
            <Route path="/leaderboard" element={<Leaderboard />} />
            <Route path="/bans" element={<BanList />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogArticle />} />
            <Route path="/admin/blog" element={<RequireAuth><AdminBlog /></RequireAuth>} />
            <Route path="/clans" element={<Clans />} />
            <Route path="/clans/:id" element={<ClanDetail />} />
            <Route path="/clans/challenges/:id" element={<ClanChallenge />} />
            <Route path="/tournaments" element={<Tournaments />} />
            <Route path="/tournaments/:slug" element={<TournamentDetail />} />
            <Route path="/tournaments/:slug/matches/:mid" element={<MatchPage />} />
            <Route path="/admin/tournaments" element={<AdminTournaments />} />
            <Route path="/ladders" element={<Ladders />} />
            <Route path="/ladders/:slug" element={<LadderDetail />} />
            <Route path="/ladders/:slug/rules" element={<LadderRules />} />
            <Route path="/ladders/:slug/matches/:mid" element={<MatchPage />} />
            <Route path="/admin/ladders" element={<AdminLadders />} />
            <Route path="/admin/patreon" element={<AdminPatreon />} />
            <Route path="/admin/seeding" element={<AdminSeeding />} />
            <Route path="/admin/overlay-updates" element={<AdminOverlayUpdates />} />
            <Route path="/rcon-tool" element={<RconToolDownload />} />
            <Route path="/admin/handbook" element={<AdminHandbook />} />
            <Route path="/admin/handbook/edit" element={<AdminHandbookEdit />} />
            <Route path="/admin/faq" element={<AdminHandbook />} />
            <Route path="/admin/sponsors" element={<AdminSponsors />} />
            <Route path="/admin/social-media" element={<AdminSocialMedia />} />
            <Route path="/admin/team" element={<RequireAuth><AdminTeam /></RequireAuth>} />
            <Route path="/team" element={<Team />} />
            <Route path="/admin/streamers" element={<RequireAuth><AdminStreamers /></RequireAuth>} />
            <Route path="/admin/servers" element={<RequireAuth><AdminServers /></RequireAuth>} />
            <Route path="/community" element={<CommunityServers />} />
            <Route path="/streamers" element={<Streamers />} />
            <Route path="/streamers/:login" element={<StreamerPage />} />
            <Route path="/streamer/studio" element={<RequireAuth><StreamerStudio /></RequireAuth>} />
            <Route path="/r/:login" element={<ReferralLanding />} />
            <Route path="/patreon" element={<Patreon />} />
            <Route path="/vip" element={<VIPLounge />} />
            <Route path="/founders-wall" element={<FoundersWall />} />
            <Route path="/lfg" element={<Navigate to="/" replace />} />
            <Route path="/database" element={<DatabasePage />} />
            <Route path="/map" element={<MapPage />} />
            <Route path="/map/:mapId" element={<MapPage />} />
            <Route path="/season" element={<SeasonPass />} />
            <Route path="/loot" element={<LootDrops />} />
            <Route path="/guides" element={<Guides />} />
            <Route path="/compare" element={<Compare />} />
            <Route path="/economy" element={<Economy />} />
            <Route path="/live" element={<LiveServer />} />
            <Route path="/seeding" element={<Seeding />} />
            <Route path="/servers" element={<Navigate to="/community" replace />} />
            <Route path="/clips" element={<Clips />} />
          <Route path="/clips/:id" element={<ClipWatch />} />
            <Route path="/screenshots" element={<Screenshots />} />
            <Route path="/patch-notes" element={<PatchNotes />} />
            <Route path="/events" element={<Events />} />
            <Route path="/challenges" element={<Challenges />} />
            <Route path="/loadouts" element={<LoadoutBuilder />} />
            <Route path="/ttk" element={<TTKCalculator />} />
            <Route path="/tierlist" element={<TierList />} />
            <Route path="/roles" element={<RoleLevels />} />
            <Route path="/matches" element={<MatchFeed />} />
            <Route path="/market" element={<Market />} />
            <Route path="/friends" element={<RequireAuth><Friends /></RequireAuth>} />
            <Route path="/profile" element={<RequireAuth><Profile /></RequireAuth>} />
            <Route path="/apply" element={<RequireAuth><ApplyAdmin /></RequireAuth>} />
            <Route path="/u/:id" element={<PublicProfile />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/about" element={<About />} />
            <Route path="/discord" element={<Discord />} />
            <Route path="/match/:id" element={<MatchRecap />} />
            <Route path="/store" element={<Store />} />
            <Route path="/store/policy" element={<StorePolicy />} />
            <Route path="/store/product/:slug" element={<StoreProduct />} />
            <Route path="/store/cart" element={<StoreCart />} />
            <Route path="/store/checkout" element={<StoreCheckout />} />
            <Route path="/store/orders" element={<RequireAuth><StoreOrders /></RequireAuth>} />
            <Route path="/store/orders/:ref" element={<StoreOrderDetail />} />
            <Route path="/store/track" element={<StoreTrackOrder />} />
            <Route path="/store/support" element={<StoreSupport />} />
            <Route path="/store/support/:ref" element={<StoreTicket />} />
            <Route path="/admin/store" element={<RequireAuth><AdminStore /></RequireAuth>} />
            <Route path="/watch/:id" element={<Watch />} />
            <Route path="/support" element={<RequireAuth><Support /></RequireAuth>} />
            <Route path="/admin" element={<RequireAuth><Admin /></RequireAuth>} />
            <Route path="/rcon" element={<RequireAuth><RconPanel /></RequireAuth>} />
            <Route path="*" element={<NotFound />} />
          </Route>
        </Routes>
        </Suspense>
      </BrowserRouter>
    </PrefsProvider>
  );
};

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <AppInner />
      </AuthProvider>
    </div>
  );
}

export default App;
